// ENUMS

export enum ContentBlock {
    Core = "core",
    PatientMgmt = "patient_mgmt",
    ELearning = "e_learning",
    ECommerce = "e_commerce",
    Social = "social",
    Payment = "payment",
    Loyalty = "loyalty"
}

export enum CoreIngestEventType {
    Identify = "identify",
    Page = "page",
    Media = "media",
    Search = "search",
    Rate = "rate"
}

export enum CoreCatalogEventType {
    User = "user",
    Media = "media",
    Site = "site"
}

export enum IdentityAction {
    Register = "register",
    Login = "login",
    Logout = "logout"
}


export enum MediaAction {
    View = 'view',
    Impression = 'impression',
    Play = 'play',
    Pause = 'pause',
    Finish = 'finish',
    Seek = 'seek'
}

export enum MediaType {
    Image = 'image',
    Video = 'video',
    Audio = 'audio'
}

export interface SearchFilter {
    [key: string]: any
}

export enum RateType {
    Order = 'order',
    Media = 'media',
    Item = 'item',
    Exam = 'exam',
    Question = 'question',
    Module = 'module',
    Process = 'process',
    Form = 'form',
    Section = 'section',
    App = 'app',
    Other = 'other',
    HCW = "hcw",
    HcwSite = "hcw_site",
    Facility = "facility",
    Assessment = "assessment",
    Customer = "customer"
}

export enum SearchItemType {
    PatientRecord = "patient_record",
    LifestylePlanItem = "lifestyle_plan_item",
    TreatmentPlanItem = "treatment_plan_item",
    PsychologicalPlanItem = "psychological_plan_item",
    CounselingPlanItem = "counseling_plan_item",
    InvestigationTestItem = "investigation_test_item",

    Blood = "blood",
    Oxygen = "oxygen",
    Drug = "drug",
    Grocery = "grocery",
    Facility = "facility",
    MedicalEquipment = "medical_equipment",
    Subscription = "subscription",
    Electronics = "electronics",
    Clothing = "clothing",
    Book = "book",

    ItemVerification = "item_verification",
    ItemReport = "item_report",
    Reward = "reward",
    Survey = "survey",

    Other = "other"
}

export enum SearchModuleType {
    Core = "core",
    ECommerce = "e_commerce",
    ELearning = "e_learning",
    Screening = "screening",
    Assessment = "assessment",
    Enrolment = "enrolment",

    MedicalReview = "medical_review",
    MyPatients = "my_patients",
    Appointments = "appointments",
    Prescription = "prescription",
    LifestyleMgmt = "lifestyle_mgmt",
    PsychologicalMgmt = "psychological_mgmt",
    CounselingMgmt = "counseling_mgmt",
    Investigation = "investigation",
    TreatmentPlan = "treatment_plan",
    Transfers = "transfers",

    Other = "other"
}

export enum NudgeScreenType {
    None = "",
    Home = "home",
    Search = "search",
    Product = "product",
    Cart = "cart",
    Checkout = "checkout",
    Reminder = "reminder",
    Favorite = "favorite",
    Other = "other"
}

export enum MaritalStatus {
    Single = "single",
    Married = "married",
    Widowed = "widowed",
    Divorced = "divorced",
    Separated = "separated",
    Other = "other",
    UnDisclosed = "undisclosed"
}

export enum MembersCount {
    None = "none",
    One = "1",
    Two = "2",
    Three = "3",
    Four = "4",
    FiveOrMore = "5_or_more",
    Undisclosed = "undisclosed"
}

export enum UserGender {
    Male = "male",
    Female = "female",
    Other = "other",
    UnDisclosed = "undisclosed"
}

export enum SiteCatalogType {
    Pharmacy = 'pharmacy',
    Clinic = 'clinic',
    Community = 'community'
}

// CATALOG OBJECTS

export type UserProperties = {
    id: string,
    name?: string,
    country?: CountryCode,
    region_state?: string,
    city?: string,
    workplace?: string,
    profession?: string,
    zipcode?: string,
    language?: LanguageCode,
    experience?: string,
    education_level?: EducationalLevel
    organization_id?: string
    organization_name?: string
    account_type?: string
    birth_year?: number
    gender?: UserGender
    marital_status?: MaritalStatus
    family_members?: MembersCount
    children_under_five?: MembersCount
}

export type MediaCatalog = {
    name?: string
    length: number, // video length in milliseconds //uint32
    description?: string,
    resolution?: string,
    language?: LanguageCode
}

export interface SiteCatalog {
    id : string,
    name: string,
    type: SiteCatalogType,
    country?: CountryCode,
    region_state?: string,
    city?: string,
    street_address?: string,
    zipcode?: string,
    level?: string,
    is_active?: boolean,
    latitude?: number,
    longitude?: number,
    culture?: string,
    parent_id?: string
    service_list?: Array<string>,
}

// EVENT PROPERTIES

export interface IdentifyProperties {
    appUserId: string,
    action: IdentityAction,
    blocked?: BlockProperties
}

export interface BlockProperties {
    reason: string
    remarks?: string
}

export interface MediaImpressionModel {
    item_properties: MediaImpressionObject
    catalog_properties: MediaCatalog
}

export interface MediaImpressionObject {
    media_id: string,
    media_type: MediaType,
    time: number,
}

export interface MediaProperties {
    id: string,
    type: MediaType,
    action: MediaAction,
    time: number
}

export interface PageProperties {
    path: string,
    title: string,
    duration: number,
    render_time: number
}

export interface SearchItemModel {
    id: string,
    type: SearchItemType,
    facility_id?: string
}

export interface SearchProperties {
    module: SearchModuleType,
    query: string,
    results_list: Array<SearchItemModel>,
    filter: Map<string, any>,
    page: number,
}

export interface RateProperties {
    rate_value: number,
    type: RateType,
    subject_id: string,
}

export enum CountryCode {

    Ascension_Island = "Ascension_Island",
    Andorra = "Andorra",
    United_Arab_Emirates = "United_Arab_Emirates",
    Afghanistan = "Afghanistan",
    Antigua_and_Barbuda = "Antigua_and_Barbuda",
    Anguilla = "Anguilla",
    Albania = "Albania",
    Armenia = "Armenia",
    Netherlands_Antilles = "Netherlands_Antilles",
    Angola = "Angola",
    Antarctica = "Antarctica",
    Argentina = "Argentina",
    American_Samoa = "American_Samoa",
    Austria = "Austria",
    Australia = "Australia",
    Aruba = "Aruba",
    Aland_Islands = "Aland_Islands",
    Azerbaijan = "Azerbaijan",
    Bosnia_and_Herzegovina = "Bosnia_and_Herzegovina",
    Barbados = "Barbados",
    Bangladesh = "Bangladesh",
    Belgium = "Belgium",
    Burkina_Faso = "Burkina_Faso",
    Bulgaria = "Bulgaria",
    Bahrain = "Bahrain",
    Burundi = "Burundi",
    Benin = "Benin",
    Saint_Barthelemy = "Saint_Barthelemy",
    Bermuda = "Bermuda",
    Brunei_Darussalam = "Brunei_Darussalam",
    Bolivia = "Bolivia",
    Caribbean_Netherlands = "Caribbean_Netherlands",
    Brazil = "Brazil",
    Bahamas = "Bahamas",
    Bhutan = "Bhutan",
    Burma = "Burma",
    Bouvet_Island = "Bouvet_Island",
    Botswana = "Botswana",
    Belarus  = "Belarus",
    Belize = "Belize",
    Canada = "Canada",
    Cocos_Islands = "Cocos_Islands",
    Democratic_Republic_Congo = "Democratic_Republic_Congo",
    Central_African_Republic = "Central_African_Republic",
    Congo = "Congo",
    Switzerland = "Switzerland",
    Ivory_Coast = "Ivory_Coast",
    Cook_Islands = "Cook_Islands",
    Chile = "Chile",
    Cameroon = "Cameroon",
    China = "China",
    Colombia = "Colombia",
    Clipperton_Island = "Clipperton_Island",
    Costa_Rica = "Costa_Rica",
    Serbia_and_Montenegro = "Serbia_and_Montenegro",
    Cuba = "Cuba",
    Cape_Verde = "Cape_Verde",
    Curacao = "Curacao",
    Christmas_Island = "Christmas_Island",
    Cyprus = "Cyprus",
    Czech_Republic = "Czech_Republic",
    Germany = "Germany",
    Diego_Garcia = "Diego_Garcia",
    Djibouti = "Djibouti",
    Denmark = "Denmark",
    Dominica = "Dominica",
    Dominican_Republic = "Dominican_Republic",
    Algeria = "Algeria",
    Ceuta_Melilla = "Ceuta_Melilla",
    Ecuador = "Ecuador",
    Estonia = "Estonia",
    Egypt = "Egypt",
    Western_Sahara = "Western_Sahara",
    Eritrea = "Eritrea",
    Spain = "Spain",
    Ethiopia = "Ethiopia",
    European_Union = "European_Union",
    Eurozone = "Eurozone",
    Finland = "Finland",
    Fiji = "Fiji",
    Malvinas = "Malvinas",
    Micronesia = "Micronesia",
    Faroe_Islands = "Faroe_Islands",
    France = "France",
    France_Metropolitan = "France_Metropolitan",
    Gabon = "Gabon",
    United_Kingdom = "United_Kingdom",
    Grenada = "Grenada",
    Georgia = "Georgia",
    French_Guiana = "French_Guiana",
    Guernsey = "Guernsey",
    Ghana = "Ghana",
    Gibraltar = "Gibraltar",
    Greenland = "Greenland",
    Gambia = "Gambia",
    Guinea = "Guinea",
    Guadeloupe = "Guadeloupe",
    Equatorial_Guinea = "Equatorial_Guinea",
    Greece = "Greece",
    South_Georgia_and_South_Sandwich_Islands = "South_Georgia_and_South_Sandwich_Islands",
    Guatemala = "Guatemala",
    Guam = "Guam",
    Guinea_Bissau = "Guinea_Bissau",
    Guyana = "Guyana",
    Hong_Kong = "Hong_Kong",
    Heard_Island_and_McDonald_Islands = "Heard_Island_and_McDonald_Islands",
    Honduras = "Honduras",
    Croatia = "Croatia",
    Haiti = "Haiti",
    Hungary = "Hungary",
    Canary_Islands = "Canary_Islands",
    Indonesia = "Indonesia",
    Ireland = "Ireland",
    Israel = "Israel",
    Isle_of_Man = "Isle_of_Man",
    India = "India",
    British_Indian_Ocean_Territory = "British_Indian_Ocean_Territory",
    Iraq = "Iraq",
    Iran = "Iran",
    Iceland = "Iceland",
    Italy = "Italy",
    Jersey = "Jersey",
    Jamaica = "Jamaica",
    Jordan = "Jordan",
    Japan = "Japan",
    Kenya = "Kenya",
    Kyrgyzstan = "Kyrgyzstan",
    Cambodia = "Cambodia",
    Kiribati = "Kiribati",
    Comoros = "Comoros",
    Saint_Kitts_and_Nevis = "Saint_Kitts_and_Nevis",
    North_Korea = "North_Korea",
    South_Korea = "South_Korea",
    Kuwait = "Kuwait",
    Cayman_Islands = "Cayman_Islands",
    Kazakhstan = "Kazakhstan",
    Lao = "Lao",
    Lebanon = "Lebanon",
    Saint_Lucia = "Saint_Lucia",
    Liechtenstein = "Liechtenstein",
    Sri_Lanka = "Sri_Lanka",
    Liberia = "Liberia",
    Lesotho = "Lesotho",
    Lithuania = "Lithuania",
    Luxembourg = "Luxembourg",
    Latvia = "Latvia",
    Libya = "Libya",
    Morocco = "Morocco",
    Monaco = "Monaco",
    Moldova = "Moldova",
    Montenegro = "Montenegro",
    Saint_Martin = "Saint_Martin",
    Madagascar = "Madagascar",
    Marshall_Islands = "Marshall_Islands",
    North_Macedonia = "North_Macedonia",
    Mali = "Mali",
    Myanmar ="Myanmar",
    Mongolia = "Mongolia",
    Macao = "Macao",
    Northern_Mariana_Islands = "Northern_Mariana_Islands",
    Martinique = "Martinique",
    Mauritania = "Mauritania",
    Montserrat = "Montserrat",
    Malta = "Malta",
    Mauritius = "Mauritius",
    Maldives = "Maldives",
    Malawi = "Malawi",
    Mexico = "Mexico",
    Malaysia = "Malaysia",
    Mozambique = "Mozambique",
    Namibia = "Namibia",
    New_Caledonia = "New_Caledonia",
    Niger = "Niger",
    Norfolk_Island = "Norfolk_Island",
    Nigeria = "Nigeria",
    Nicaragua = "Nicaragua",
    Netherlands = "Netherlands",
    Norway = "Norway",
    Nepal = "Nepal",
    Nauru = "Nauru",
    Neutral_Zone = "Neutral_Zone",
    Niue = "Niue",
    New_Zealand = "New_Zealand",
    Oman = "Oman",
    Panama = "Panama",
    Peru = "Peru",
    French_Polynesia = "French_Polynesia",
    Papua_New_Guinea = "Papua_New_Guinea",
    Philippines = "Philippines",
    Pakistan = "Pakistan",
    Poland = "Poland",
    Saint_Pierre_and_Miquelon = "Saint_Pierre_and_Miquelon",
    Pitcairn = "Pitcairn",
    Puerto_Rico = "Puerto_Rico",
    Palestine = "Palestine",
    Portugal = "Portugal",
    Palau = "Palau",
    Paraguay = "Paraguay",
    Qatar = "Qatar",
    Reunion = "Reunion",
    Romania = "Romania",
    Serbia = "Serbia",
    Russia = "Russia",
    Rwanda = "Rwanda",
    Saudi_Arabia = "Saudi_Arabia",
    Solomon_Islands = "Solomon_Islands",
    Seychelles = "Seychelles",
    Sudan = "Sudan",
    Sweden = "Sweden",
    Singapore = "Singapore",
    Saint_Helena = "Saint_Helena",
    Slovenia = "Slovenia",
    Svalbard = "Svalbard",
    Slovakia = "Slovakia",
    Sierra_Leone = "Sierra_Leone",
    San_Marino = "San_Marino",
    Senegal = "Senegal",
    Somalia = "Somalia",
    Suriname = "Suriname",
    South_Sudan = "South_Sudan",
    Sao_Tome_and_Principe = "Sao_Tome_and_Principe",
    USSR = "USSR",
    El_Salvador = "El_Salvador",
    Sint_Maarten = "Sint_Maarten",
    Syria = "Syria",
    Eswatini = "Eswatini",
    Turks_and_Caicos_Islands = "Turks_and_Caicos_Islands",
    French_Southern_Territories = "French_Southern_Territories",
    Togo = "Togo",
    Thailand = "Thailand",
    Tajikistan = "Tajikistan",
    Tokelau = "Tokelau",
    Timor_Leste = "Timor_Leste",
    Turkmenistan = "Turkmenistan",
    Tunisia = "Tunisia",
    Tonga = "Tonga",
    East_Timor = "East_Timor",
    Turkey = "Turkey",
    Trinidad_and_Tobago = "Trinidad_and_Tobago",
    Tuvalu = "Tuvalu",
    Taiwan = "Taiwan",
    Tanzania = "Tanzania",
    Ukraine = "Ukraine",
    Uganda = "Uganda",
    United_States_Minor_Outlying_Islands = "United_States_Minor_Outlying_Islands",
    United_States = "United_States",
    Uruguay = "Uruguay",
    Uzbekistan = "Uzbekistan",
    Vatican = "Vatican",
    Saint_Vincent_and_Grenadines = "Saint_Vincent_and_Grenadines",
    Venezuela = "Venezuela",
    British_Virgin_Islands = "British_Virgin_Islands",
    US_Virgin_Islands = "US_Virgin_Islands",
    Vietnam = "Vietnam",
    Vanuatu = "Vanuatu",
    Wallis_and_Futuna = "Wallis_and_Futuna",
    Samoa = "Samoa",
    Northern_Ireland = "Northern_Ireland",
    Kosovo = "Kosovo",
    Yemen = "Yemen",
    Mayotte = "Mayotte",
    Yugoslavia = "Yugoslavia",
    South_Africa = "South_Africa",
    Zambia = "Zambia",
    Zaire = "Zaire",
    Zimbabwe = "Zimbabwe"
}

export enum EducationalLevel {
    Primary = "primary",
    LowerSecondary = "lower_secondary",
    UpperSecondary = "upper_secondary",
    NonTertiary = "non_tertiary",
    Tertiary = "tertiary",
    Bachelors = "bachelors",
    Masters = "masters",
    Doctorate = "doctorate"
}

export enum LanguageCode{
    Afar = "Afar",
    Abkhaz = "Abkhaz",
    Avestan = "Avestan",
    Afrikaans = "Afrikaans",
    Akan = "Akan",
    Amharic = "Amharic",
    Aragonese = "Aragonese",
    Arabic = "Arabic",
    Avaric = "Avaric",
    Aymara = "Aymara",
    Azerbaijani = "Azerbaijani",
    Bashkir = "Bashkir",
    Belarusian = "Belarusian",
    Bulgarian = "Bulgarian",
    Bihari = "Bihari",
    Bislama = "Bislama",
    Bambara = "Bambara",
    Bengali = "Bengali",
    Tibetan = "Tibetan",
    Breton = "Breton",
    Bosnian = "Bosnian",
    Catalan = "Catalan",
    Chechen = "Chechen",
    Chamorro = "Chamorro",
    Corsican = "Corsican",
    Cree = "Cree",
    Czech = "Czech",
    Slavonic = "Slavonic",
    Chuvash = "Chuvash",
    Welsh = "Welsh",
    Danish = "Danish",
    German = "German",
    Maldivian = "Maldivian",
    Dzongkha = "Dzongkha",
    Ewe = "Ewe",
    Greek = "Greek",
    English ="English",
    Esperanto = "Esperanto",
    Spanish = "Spanish",
    Estonian = "Estonian",
    Basque = "Basque",
    Persian = "Persian",
    Fulah = "Fulah",
    Finnish = "Finnish",
    Fijian = "Fijian",
    Faroese = "Faroese",
    French = "French",
    Western_Frisian = "Western_Frisian",
    Irish = "Irish",
    Gaelic = "Gaelic",
    Galician = "Galician",
    Guarani = "Guarani",
    Gujarati = "Gujarati",
    Manx = "Manx",
    Hausa = "Hausa",
    Hebrew = "Hebrew",
    Hindi = "Hindi",
    Hiri_Motu = "Hiri_Motu",
    Croatian = "Croatian",
    Haitian = "Haitian",
    Hungarian = "Hungarian",
    Armenian = "Armenian",
    Herero ="Herero",
    Interlingua = "Interlingua",
    Indonesian = "Indonesian",
    Occidental = "Occidental",
    Igbo = "Igbo",
    Nuosu = "Nuosu",
    Inupiaq = "Inupiaq",
    Ido = "Ido",
    Icelandic = "Icelandic",
    Italian = "Italian",
    Inuktitut = "Inuktitut",
    Japanese = "Japanese",
    Javanese = "Javanese",
    Georgian = "Georgian",
    Kongo = "Kongo",
    Kikuyu = "Kikuyu",
    Kuanyama = "Kuanyama",
    Kazakh = "Kazakh",
    Greenlandic = "Greenlandic",
    Central_Khmer = "Central_Khmer",
    Kannada = "Kannada",
    Korean = "Korean",
    Kanuri = "Kanuri",
    Kashmiri = "Kashmiri",
    Kurdish = "Kurdish",
    Komi = "Komi",
    Cornish = "Cornish",
    Kirghiz = "Kirghiz",
    Latin = "Latin",
    Luxembourgish = "Luxembourgish",
    Ganda = "Ganda",
    Limburgish = "Limburgish",
    Lingala ="Lingala",
    Lao = "Lao",
    Lithuanian = "Lithuanian",
    Luba_katanga = "Luba_katanga",
    Latvian = "Latvian",
    Malagasy = "Malagasy",
    Marshallese = "Marshallese",
    Maori = "Maori",
    Macedonian = "Macedonian",
    Malayalam = "Malayalam",
    Mongolian = "Mongolian",
    Moldavian = "Moldavian",
    Marathi = "Marathi",
    Malay = "Malay",
    Maltese = "Maltese",
    Burmese = "Burmese",
    Nauruan = "Nauruan",
    Norwegian_Bokmal = "Norwegian_Bokmal",
    North_Ndebele = "North_Ndebele",
    Nepali = "Nepali",
    Ndonga = "Ndonga",
    Dutch = "Dutch",
    Norwegian_Nynorsk = "Norwegian_Nynorsk",
    Norwegian = "Norwegian",
    South_Ndebele = "South_Ndebele",
    Navajo = "Navajo",
    Nyanja = "Nyanja",
    Occitan = "Occitan",
    Ojibwa = "Ojibwa",
    Oromo = "Oromo",
    Oriya = "Oriya",
    Ossetian = "Ossetian",
    Punjabi = "Punjabi",
    Pali = "Pali",
    Polish = "Polish",
    Pushto = "Pushto",
    Portuguese = "Portuguese",
    Quechua = "Quechua",
    Reunionese = "Reunionese",
    Romansh = "Romansh",
    Rundi = "Rundi",
    Romanian = "Romanian",
    Russian = "Russian",
    Kinyarwanda = "Kinyarwanda",
    Sanskrit = "Sanskrit",
    Sardinian = "Sardinian",
    Sindhi = "Sindhi",
    Northern_Sami = "Northern Sami",
    Sango = "Sango",
    Slavic = "Slavic_Languages",
    Sinhalese = "Sinhalese",
    Slovak = "Slovak",
    Slovenian = "Slovenian",
    Samoan = "Samoan",
    Shona = "Shona",
    Somali = "Somali",
    Albanian = "Albanian",
    Serbian = "Serbian",
    Swati = "Swati",
    Southern_Sotho = "Southern Sotho",
    Sundanese =  "Sundanese",
    Swedish = "Swedish",
    Swahili = "Swahili",
    Tamil = "Tamil",
    Telugu = "Telugu",
    Tajik = "Tajik",
    Thai = "Thai",
    Tigrinya = "Tigrinya",
    Turkmen = "Turkmen",
    Tagalog = "Tagalog",
    Tswana = "Tswana",
    Tongan = "Tongan",
    Turkish = "Turkish",
    Tsonga = "Tsonga",
    Tatar = "Tatar",
    Twi = "Twi",
    Tahitian = "Tahitian",
    Uighur = "Uighur",
    Ukrainian = "Ukrainian",
    Urdu = "Urdu",
    Uzbek = "Uzbek",
    Venda = "Venda",
    Vietnamese = "Vietnamese",
    Volapuk = "Volapuk",
    Walloon = "Walloon",
    Wolof = "Wolof",
    Xhosa = "Xhosa",
    Yiddish = "Yiddish",
    Yoruba = "Yoruba",
    Zhuang = "Zhuang",
    Chinese = "Chinese",
    Zulu = "Zulu"
}
